import * as React from 'react';
import RcTreeSelect, { SHOW_ALL, SHOW_CHILD, SHOW_PARENT, TreeNode, } from '@rc-component/tree-select';
import { omit } from '@rc-component/util';
import { clsx } from 'clsx';
import { useAllowClear, useZIndex } from '../_util/hooks';
import { useMergeSemantic, useSemanticRootStyle } from '../_util/hooks/useMergeSemantic';
import { getTransitionName } from '../_util/motion';
import genPurePanel from '../_util/PurePanel';
import { getMergedStatus, getStatusClassNames } from '../_util/statusUtils';
import { devUseWarning } from '../_util/warning';
import { ConfigContext } from '../config-provider';
import { useComponentConfig } from '../config-provider/context';
import DefaultRenderEmpty from '../config-provider/defaultRenderEmpty';
import DisabledContext from '../config-provider/DisabledContext';
import useCSSVarCls from '../config-provider/hooks/useCSSVarCls';
import useSize from '../config-provider/hooks/useSize';
import { FormItemInputContext } from '../form/context';
import useVariant from '../form/hooks/useVariants';
import { useLocale } from '../locale';
import mergedBuiltinPlacements from '../select/mergedBuiltinPlacements';
import useSelectStyle from '../select/style';
import useIcons from '../select/useIcons';
import usePopupRender from '../select/usePopupRender';
import useShowArrow from '../select/useShowArrow';
import { useCompactItemContext } from '../space/Compact';
import { useToken } from '../theme/internal';
import SwitcherIconCom from '../tree/utils/iconUtil';
import useStyle from './style';
const InternalTreeSelect = (props, ref) => {
    const { prefixCls: customizePrefixCls, size: customizeSize, disabled: customDisabled, bordered = true, style, className, rootClassName, treeCheckable, multiple, listHeight = 256, listItemHeight: customListItemHeight, placement, notFoundContent, switcherIcon: customSwitcherIcon, treeLine, getPopupContainer, popupClassName, dropdownClassName, treeIcon = false, transitionName, choiceTransitionName = '', status: customStatus, treeExpandAction, builtinPlacements, dropdownMatchSelectWidth, popupMatchSelectWidth, allowClear, variant: customVariant, dropdownStyle: _dropdownStyle, dropdownRender, popupRender, onDropdownVisibleChange, onOpenChange, tagRender, maxCount, showCheckedStrategy, treeCheckStrictly, styles, classNames, suffix: customSuffix, suffixIcon: customSuffixIcon, showArrow, ...restProps } = props;
    const [locale] = useLocale('global');
    const { getPrefixCls, getPopupContainer: getContextPopupContainer, direction, className: contextClassName, style: contextStyle, styles: contextStyles, classNames: contextClassNames, switcherIcon, allowClear: contextAllowClear, } = useComponentConfig('treeSelect');
    const { renderEmpty, virtual, popupMatchSelectWidth: contextPopupMatchSelectWidth, popupOverflow, } = React.useContext(ConfigContext);
    const [, token] = useToken();
    const listItemHeight = customListItemHeight ?? token?.controlHeightSM + token?.paddingXXS;
    if (process.env.NODE_ENV !== 'production') {
        const warning = devUseWarning('TreeSelect');
        const deprecatedProps = {
            dropdownMatchSelectWidth: 'popupMatchSelectWidth',
            dropdownStyle: 'styles.popup.root',
            dropdownClassName: 'classNames.popup.root',
            popupClassName: 'classNames.popup.root',
            dropdownRender: 'popupRender',
            onDropdownVisibleChange: 'onOpenChange',
            bordered: 'variant',
            suffixIcon: 'suffix',
        };
        Object.entries(deprecatedProps).forEach(([oldProp, newProp]) => {
            warning.deprecated(!(oldProp in props), oldProp, newProp);
        });
        warning(multiple !== false || !treeCheckable, 'usage', '`multiple` will always be `true` when `treeCheckable` is true');
        warning(!('showArrow' in props), 'deprecated', '`showArrow` is deprecated which will be removed in next major version. It will be a default behavior, you can hide it by setting `suffix` to null.');
    }
    const rootPrefixCls = getPrefixCls();
    const prefixCls = getPrefixCls('select', customizePrefixCls);
    const treePrefixCls = getPrefixCls('select-tree', customizePrefixCls);
    const treeSelectPrefixCls = getPrefixCls('tree-select', customizePrefixCls);
    const { compactSize, compactItemClassnames } = useCompactItemContext(prefixCls, direction);
    const rootCls = useCSSVarCls(prefixCls);
    const treeSelectRootCls = useCSSVarCls(treeSelectPrefixCls);
    const [hashId, cssVarCls] = useSelectStyle(prefixCls, rootCls);
    useStyle(treeSelectPrefixCls, treePrefixCls, treeSelectRootCls);
    const [variant, enableVariantCls] = useVariant('treeSelect', customVariant, bordered);
    // ===================== Size =====================
    const mergedSize = useSize((ctx) => customizeSize ?? compactSize ?? ctx);
    // ===================== Disabled =====================
    const disabled = React.useContext(DisabledContext);
    const mergedDisabled = customDisabled ?? disabled;
    // ===================== Form =====================
    const { status: contextStatus, hasFeedback, isFormItemInput, feedbackIcon, } = React.useContext(FormItemInputContext);
    const mergedStatus = getMergedStatus(contextStatus, customStatus);
    // =========== Merged Props for Semantic ===========
    const mergedProps = {
        ...props,
        size: mergedSize,
        disabled: mergedDisabled,
        status: mergedStatus,
        variant,
    };
    const contextStyleRoot = useSemanticRootStyle(contextStyle);
    const styleRoot = useSemanticRootStyle(style);
    const [mergedClassNames, mergedStyles] = useMergeSemantic([contextClassNames, classNames], [contextStyles, contextStyleRoot, styles, styleRoot], {
        props: mergedProps,
    }, {
        popup: {
            _default: 'root',
        },
    });
    const mergedPopupClassName = clsx(popupClassName || dropdownClassName, `${treeSelectPrefixCls}-dropdown`, {
        [`${treeSelectPrefixCls}-dropdown-rtl`]: direction === 'rtl',
    }, rootClassName, mergedClassNames.popup?.root, cssVarCls, rootCls, treeSelectRootCls, hashId);
    const mergedPopupRender = usePopupRender(popupRender || dropdownRender);
    const mergedOnOpenChange = onOpenChange || onDropdownVisibleChange;
    const isMultiple = !!(treeCheckable || multiple);
    const mergedMaxCount = React.useMemo(() => {
        if (maxCount &&
            ((showCheckedStrategy === 'SHOW_ALL' && !treeCheckStrictly) ||
                showCheckedStrategy === 'SHOW_PARENT')) {
            return undefined;
        }
        return maxCount;
    }, [maxCount, showCheckedStrategy, treeCheckStrictly]);
    const mergedCustomSuffix = customSuffix !== undefined ? customSuffix : customSuffixIcon;
    const showSuffix = useShowArrow(mergedCustomSuffix, showArrow);
    const mergedPopupMatchSelectWidth = popupMatchSelectWidth ?? dropdownMatchSelectWidth ?? contextPopupMatchSelectWidth;
    // ===================== Icons =====================
    const { suffix, removeIcon } = useIcons({
        componentName: 'TreeSelect',
        ...restProps,
        suffix: mergedCustomSuffix,
        multiple: isMultiple,
        showSuffix,
        showArrow,
        hasFeedback,
        feedbackIcon,
        prefixCls,
    });
    const mergedAllowClear = useAllowClear({
        allowClear,
        contextAllowClear,
        clearIcon: restProps.clearIcon,
        defaultLabel: locale.clear,
        componentName: 'TreeSelect',
    });
    // ===================== Empty =====================
    let mergedNotFound;
    if (notFoundContent !== undefined) {
        mergedNotFound = notFoundContent;
    }
    else {
        mergedNotFound = renderEmpty?.('TreeSelect') || (React.createElement(DefaultRenderEmpty, { componentName: "TreeSelect" }));
    }
    // ==================== Render =====================
    const selectProps = omit(restProps, ['removeIcon', 'clearIcon']);
    // ===================== Placement =====================
    const memoizedPlacement = React.useMemo(() => {
        if (placement !== undefined) {
            return placement;
        }
        return direction === 'rtl' ? 'bottomRight' : 'bottomLeft';
    }, [placement, direction]);
    const mergedClassName = clsx(!customizePrefixCls && treeSelectPrefixCls, {
        [`${prefixCls}-lg`]: mergedSize === 'large',
        [`${prefixCls}-sm`]: mergedSize === 'small',
        [`${prefixCls}-rtl`]: direction === 'rtl',
        [`${prefixCls}-${variant}`]: enableVariantCls,
        [`${prefixCls}-in-form-item`]: isFormItemInput,
    }, getStatusClassNames(prefixCls, mergedStatus, hasFeedback), compactItemClassnames, contextClassName, className, rootClassName, mergedClassNames?.root, cssVarCls, rootCls, treeSelectRootCls, hashId);
    const mergedSwitcherIcon = customSwitcherIcon ?? switcherIcon;
    const renderSwitcherIcon = (nodeProps) => (React.createElement(SwitcherIconCom, { prefixCls: treePrefixCls, switcherIcon: mergedSwitcherIcon, treeNodeProps: nodeProps, showLine: treeLine }));
    // ============================ zIndex ============================
    const [zIndex] = useZIndex('SelectLike', mergedStyles.popup?.root?.zIndex);
    return (React.createElement(RcTreeSelect, { classNames: mergedClassNames, styles: mergedStyles, virtual: virtual, disabled: mergedDisabled, ...selectProps, popupMatchSelectWidth: mergedPopupMatchSelectWidth, builtinPlacements: mergedBuiltinPlacements(builtinPlacements, popupOverflow), ref: ref, prefixCls: prefixCls, className: mergedClassName, style: mergedStyles.root, listHeight: listHeight, listItemHeight: listItemHeight, treeCheckable: treeCheckable ? React.createElement("span", { className: `${prefixCls}-tree-checkbox-inner` }) : treeCheckable, treeLine: !!treeLine, suffix: suffix, multiple: isMultiple, placement: memoizedPlacement, removeIcon: removeIcon, allowClear: mergedAllowClear, switcherIcon: renderSwitcherIcon, showTreeIcon: treeIcon, notFoundContent: mergedNotFound, getPopupContainer: getPopupContainer || getContextPopupContainer, treeMotion: null, popupClassName: mergedPopupClassName, popupStyle: { ...mergedStyles.popup?.root, zIndex }, popupRender: mergedPopupRender, onPopupVisibleChange: mergedOnOpenChange, choiceTransitionName: getTransitionName(rootPrefixCls, '', choiceTransitionName), transitionName: getTransitionName(rootPrefixCls, 'slide-up', transitionName), treeExpandAction: treeExpandAction, tagRender: isMultiple ? tagRender : undefined, maxCount: mergedMaxCount, showCheckedStrategy: showCheckedStrategy, treeCheckStrictly: treeCheckStrictly }));
};
const TreeSelectRef = React.forwardRef(InternalTreeSelect);
const TreeSelect = TreeSelectRef;
// We don't care debug panel
/* istanbul ignore next */
const PurePanel = genPurePanel(TreeSelect, 'popupAlign', (props) => omit(props, ['visible']));
TreeSelect.TreeNode = TreeNode;
TreeSelect.SHOW_ALL = SHOW_ALL;
TreeSelect.SHOW_PARENT = SHOW_PARENT;
TreeSelect.SHOW_CHILD = SHOW_CHILD;
TreeSelect._InternalPanelDoNotUseOrYouWillBeFired = PurePanel;
if (process.env.NODE_ENV !== 'production') {
    TreeSelect.displayName = 'TreeSelect';
}
export { TreeNode };
export default TreeSelect;
